/**
 * Simpatico Header JavaScript
 *
 * Handles mobile menu toggle, scroll behavior, and keyboard navigation.
 *
 * @package Simpaticobike
 * @since 3.0.0
 */

(function() {
	'use strict';

	// DOM Elements
	const header = document.querySelector('.simpatico-header');
	const toggle = document.querySelector('.simpatico-header__toggle');
	const mobileNav = document.querySelector('.simpatico-header__mobile-nav');
	const body = document.body;
	const docEl = document.documentElement;

	// State
	let isMenuOpen = false;
	let headerHeightRaf = 0;
	let scrollRaf = 0;
	let resizeObserver = null;
	let lastHeaderHeight = 0;
	const scrollThreshold = 50;

	/**
	 * Initialize header functionality
	 */
	function init() {
		if (!header) return;

		requestMobileHeaderHeightUpdate();
		window.addEventListener('resize', handleResize, { passive: true });

		if ('ResizeObserver' in window) {
			resizeObserver = new ResizeObserver(() => {
				requestMobileHeaderHeightUpdate();
			});
			resizeObserver.observe(header);
		}

		// Mobile menu toggle
		if (toggle && mobileNav) {
			initMobileSubmenus();
			toggle.addEventListener('click', toggleMobileMenu);

			// Close menu on escape key
			document.addEventListener('keydown', function(e) {
				if (e.key === 'Escape' && isMenuOpen) {
					closeMobileMenu();
				}
			});

			// Close menu when clicking outside
			mobileNav.addEventListener('click', function(e) {
				if (e.target === mobileNav) {
					closeMobileMenu();
				}
			});

			// Close menu on link click
			const mobileLinks = mobileNav.querySelectorAll('a');
			mobileLinks.forEach(function(link) {
				link.addEventListener('click', closeMobileMenu);
			});

			const mobileSocial = document.querySelector('.simpatico-header__mobile-social');
			if (mobileSocial && mobileNav.querySelector('.simpatico-mobile-nav a[href*="instagram.com"]')) {
				mobileSocial.style.display = 'none';
			}
		}

		// Scroll behavior for transparent/hero header layouts.
		if (shouldTrackHeaderScroll()) {
			window.addEventListener('scroll', requestScrollUpdate, { passive: true });
			requestScrollUpdate();
		}

		// Keyboard navigation for dropdowns
		initDropdownKeyboard();
	}

	/**
	 * Toggle mobile menu
	 */
	function toggleMobileMenu() {
		isMenuOpen = !isMenuOpen;

		toggle.setAttribute('aria-expanded', isMenuOpen.toString());
		mobileNav.setAttribute('aria-hidden', (!isMenuOpen).toString());

		if (isMenuOpen) {
			body.classList.add('simpatico-mobile-menu-open');
			docEl.classList.add('simpatico-mobile-menu-open');
			// Focus first menu item
			const firstLink = mobileNav.querySelector('a');
			if (firstLink) {
				setTimeout(function() {
					firstLink.focus();
				}, 100);
			}
		} else {
			body.classList.remove('simpatico-mobile-menu-open');
			docEl.classList.remove('simpatico-mobile-menu-open');
		}
	}

	/**
	 * Close mobile menu
	 */
	function closeMobileMenu() {
		if (!isMenuOpen) return;

		isMenuOpen = false;
		toggle.setAttribute('aria-expanded', 'false');
		mobileNav.setAttribute('aria-hidden', 'true');
		body.classList.remove('simpatico-mobile-menu-open');
		docEl.classList.remove('simpatico-mobile-menu-open');

		const openSubmenus = mobileNav.querySelectorAll('.menu-item-has-children.is-open');
		openSubmenus.forEach(function(item) {
			item.classList.remove('is-open');
			const submenu = item.querySelector('.sub-menu');
			const toggleButton = item.querySelector('.simpatico-mobile-nav__toggle');
			if (submenu) {
				submenu.setAttribute('hidden', '');
			}
			if (toggleButton) {
				toggleButton.setAttribute('aria-expanded', 'false');
			}
		});

		toggle.focus();
	}

	/**
	 * Keep mobile nav offset synced with current header height.
	 */
	function updateMobileHeaderHeight() {
		if (!header) return;
		const nextHeight = Math.max(56, Math.round(header.getBoundingClientRect().height || 74));
		if (nextHeight === lastHeaderHeight) return;
		lastHeaderHeight = nextHeight;
		docEl.style.setProperty('--simpatico-mobile-header-height', `${nextHeight}px`);
	}

	function requestMobileHeaderHeightUpdate() {
		if (headerHeightRaf) return;
		headerHeightRaf = window.requestAnimationFrame(() => {
			headerHeightRaf = 0;
			updateMobileHeaderHeight();
		});
	}

	/**
	 * Handle viewport resize changes.
	 */
	function handleResize() {
		requestMobileHeaderHeightUpdate();
		if (window.innerWidth > 1023 && isMenuOpen) {
			closeMobileMenu();
		}
	}

	/**
	 * Initialize mobile submenu toggles
	 */
	function initMobileSubmenus() {
		if (!mobileNav) return;

		const submenuItems = mobileNav.querySelectorAll('.menu-item-has-children');
		submenuItems.forEach(function(item, index) {
			const submenu = item.querySelector('.sub-menu');
			const link = item.querySelector('a');

			if (!submenu || item.querySelector('.simpatico-mobile-nav__toggle')) return;

			const submenuId = submenu.id || 'simpatico-submenu-' + (index + 1);
			submenu.id = submenuId;
			submenu.setAttribute('hidden', '');
			item.classList.remove('is-open');

			const toggleButton = document.createElement('button');
			toggleButton.type = 'button';
			toggleButton.className = 'simpatico-mobile-nav__toggle';
			toggleButton.setAttribute('aria-expanded', 'false');
			toggleButton.setAttribute('aria-controls', submenuId);
			toggleButton.setAttribute(
				'aria-label',
				'Toggle ' + (link ? link.textContent.trim() : 'submenu') + ' submenu'
			);
			toggleButton.innerHTML = '<span class="simpatico-mobile-nav__toggle-icon" aria-hidden="true"></span>';

			if (link) {
				link.after(toggleButton);
			} else {
				item.insertBefore(toggleButton, submenu);
			}

			toggleButton.addEventListener('click', function(event) {
				event.preventDefault();
				const isOpen = item.classList.toggle('is-open');
				toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
				if (isOpen) {
					submenu.removeAttribute('hidden');
				} else {
					submenu.setAttribute('hidden', '');
				}
			});
		});
	}

	/**
	 * Handle scroll for transparent header
	 */
	function shouldTrackHeaderScroll() {
		return (
			body.classList.contains('simpatico-has-hero') ||
			body.classList.contains('simpatico-configurator-page')
		);
	}

	function handleScroll() {
		body.classList.toggle('scrolled', window.scrollY > scrollThreshold);
	}

	function requestScrollUpdate() {
		if (scrollRaf) return;
		scrollRaf = window.requestAnimationFrame(() => {
			scrollRaf = 0;
			handleScroll();
		});
	}

	/**
	 * Initialize keyboard navigation for dropdowns
	 */
	function initDropdownKeyboard() {
		const dropdownItems = document.querySelectorAll('.simpatico-nav__item--has-dropdown');

		dropdownItems.forEach(function(item) {
			const link = item.querySelector('.simpatico-nav__link');
			const dropdown = item.querySelector('.simpatico-nav__dropdown');
			const toggleButton = item.querySelector('.simpatico-nav__dropdown-toggle');

			if (!link || !dropdown || !toggleButton) return;

			function setOpen(isOpen) {
				item.classList.toggle('is-open', isOpen);
				toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
				if (isOpen) {
					dropdown.style.opacity = '1';
					dropdown.style.visibility = 'visible';
					dropdown.style.transform = 'translateX(-50%) translateY(0)';
				} else {
					dropdown.style.removeProperty('opacity');
					dropdown.style.removeProperty('visibility');
					dropdown.style.removeProperty('transform');
				}
			}

			function focusFirstDropdownLink() {
				const firstDropdownLink = dropdown.querySelector('a');
				if (firstDropdownLink) {
					setOpen(true);
					firstDropdownLink.focus();
				}
			}

			toggleButton.addEventListener('click', function() {
				setOpen(!item.classList.contains('is-open'));
			});

			toggleButton.addEventListener('keydown', function(e) {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					focusFirstDropdownLink();
				}
				if (e.key === 'Escape') {
					setOpen(false);
					link.focus();
				}
			});

			document.addEventListener('pointerdown', function(e) {
				if (!item.contains(e.target)) {
					setOpen(false);
				}
			});

			link.addEventListener('keydown', function(e) {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					focusFirstDropdownLink();
				}
			});

			// Navigate within dropdown
			const dropdownLinks = dropdown.querySelectorAll('a');
			dropdownLinks.forEach(function(dropdownLink, index) {
				dropdownLink.addEventListener('keydown', function(e) {
					if (e.key === 'ArrowDown') {
						e.preventDefault();
						const nextLink = dropdownLinks[index + 1];
						if (nextLink) {
							nextLink.focus();
						}
					}

					if (e.key === 'ArrowUp') {
						e.preventDefault();
						const prevLink = dropdownLinks[index - 1];
						if (prevLink) {
							prevLink.focus();
						} else {
							toggleButton.focus();
						}
					}

					if (e.key === 'Escape') {
						setOpen(false);
						toggleButton.focus();
					}
				});
			});
		});
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
